import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import MaturaFunctionGraph from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import { buildInequalityHighlightBands } from "./MaturaPiecewiseInequalityExplanationGraph";
import {
  formatLinear,
  parseNumberFromAny,
  parseSingleInterval,
} from "./helpers/MaturaPiecewiseFunctionUtils";

const EPS = 1e-9;
const COMPARISON_LATEX = { ">": ">", ">=": "\\ge", "<": "<", "<=": "\\le" };
const ORIGINAL = {
  left: -4,
  join: 2,
  right: 5,
  a1: 1,
  b1: 2,
  a2: -1,
  b2: 5,
  rightClosed: false,
  equationY: 3,
  equationX: 1,
  windowLeft: 2,
  windowRight: 3,
  windowMax: 4,
  threshold: 1,
  comparisonOperator: ">",
  comparisonInterval: { left: -1, right: 4, leftClosed: false, rightClosed: false },
};

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function valueAt(data, x) {
  return x <= data.join ? data.a1 * x + data.b1 : data.a2 * x + data.b2;
}

function intervalLatex(interval) {
  const leftBracket = interval.leftClosed ? "[" : "(";
  const rightBracket = interval.rightClosed ? "]" : ")";
  return `${leftBracket}${interval.left},${interval.right}${rightBracket}`;
}

function intersectIntervals(first, second) {
  const left = Math.max(first.left, second.left);
  const right = Math.min(first.right, second.right);
  const leftClosed =
    (left !== first.left || first.leftClosed) &&
    (left !== second.left || second.leftClosed);
  const rightClosed =
    (right !== first.right || first.rightClosed) &&
    (right !== second.right || second.rightClosed);

  if (left > right || (left === right && !(leftClosed && rightClosed))) return null;
  return { left, right, leftClosed, rightClosed };
}

function mergeIntervals(intervals) {
  const sorted = intervals.filter(Boolean).sort((a, b) => a.left - b.left);
  if (!sorted.length) return [];

  return sorted.slice(1).reduce((merged, next) => {
    const current = merged[merged.length - 1];
    const touches = next.left === current.right && (current.rightClosed || next.leftClosed);
    if (next.left < current.right || touches) {
      if (next.right > current.right) {
        current.right = next.right;
        current.rightClosed = next.rightClosed;
      } else if (next.right === current.right) {
        current.rightClosed = current.rightClosed || next.rightClosed;
      }
      return merged;
    }
    merged.push({ ...next });
    return merged;
  }, [{ ...sorted[0] }]);
}

function branchRange(a, b, domain) {
  const leftY = a * domain.left + b;
  const rightY = a * domain.right + b;
  return a > 0
    ? { left: leftY, right: rightY, leftClosed: domain.leftClosed, rightClosed: domain.rightClosed }
    : { left: rightY, right: leftY, leftClosed: domain.rightClosed, rightClosed: domain.leftClosed };
}

function branchComparison(a, b, domain, operator, threshold) {
  const root = (threshold - b) / a;
  const equality = operator.includes("=");
  const greater = operator.startsWith(">");
  const solutionOnRight = (a > 0) === greater;
  const inequalityInterval = solutionOnRight
    ? { left: root, right: Infinity, leftClosed: equality, rightClosed: false }
    : { left: -Infinity, right: root, leftClosed: false, rightClosed: equality };
  return intersectIntervals(domain, inequalityInterval);
}

function findEquation(data) {
  const structuralValues = [
    data.a1 * data.left + data.b1,
    data.a1 * data.join + data.b1,
    data.a2 * data.join + data.b2,
    data.a2 * data.right + data.b2,
  ];
  const candidates = [-4, -3, -2, -1, 1, 2, 3, 4]
    .filter((y) => !structuralValues.includes(y))
    .sort(() => Math.random() - 0.5);

  for (const equationY of candidates) {
    const firstRoot = (equationY - data.b1) / data.a1;
    const secondRoot = (equationY - data.b2) / data.a2;
    const roots = [];
    if (firstRoot >= data.left && firstRoot <= data.join) roots.push(firstRoot);
    if (secondRoot > data.join && secondRoot <= data.right) roots.push(secondRoot);
    const rootIsInsideFirstPiece = roots[0] > data.left && roots[0] < data.join;
    const rootIsInsideSecondPiece = roots[0] > data.join && roots[0] < data.right;
    if (roots.length === 1 && (rootIsInsideFirstPiece || rootIsInsideSecondPiece)) {
      return { equationY, equationX: roots[0] };
    }
  }
  return null;
}

function findComparison(data) {
  const structuralValues = [
    data.a1 * data.left + data.b1,
    data.a1 * data.join + data.b1,
    data.a2 * data.join + data.b2,
    data.a2 * data.right + data.b2,
  ];
  const candidates = [];
  [">", ">=", "<", "<="].forEach((operator) => {
    for (let threshold = -4; threshold <= 4; threshold += 1) {
      if (!structuralValues.includes(threshold)) candidates.push({ operator, threshold });
    }
  });

  for (const candidate of candidates.sort(() => Math.random() - 0.5)) {
    const intervals = mergeIntervals([
      branchComparison(
        data.a1,
        data.b1,
        { left: data.left, right: data.join, leftClosed: true, rightClosed: true },
        candidate.operator,
        candidate.threshold
      ),
      branchComparison(
        data.a2,
        data.b2,
        { left: data.join, right: data.right, leftClosed: false, rightClosed: true },
        candidate.operator,
        candidate.threshold
      ),
    ]);
    const interval = intervals[0];
    const hasInteriorBoundary = interval
      && (interval.left > data.left || interval.right < data.right);
    const hasIntegerEnds = interval
      && Number.isInteger(interval.left)
      && Number.isInteger(interval.right);
    if (
      intervals.length === 1
      && interval.right - interval.left >= 1
      && hasInteriorBoundary
      && hasIntegerEnds
    ) {
      return {
        threshold: candidate.threshold,
        comparisonOperator: candidate.operator,
        comparisonInterval: interval,
      };
    }
  }
  return null;
}

function matchesRangeRelationship(first, second, relationship) {
  const firstCoversSecond = first.left <= second.left && first.right >= second.right;
  const secondCoversFirst = second.left <= first.left && second.right >= first.right;
  const firstContainsSecond =
    firstCoversSecond &&
    (first.left < second.left || first.right > second.right);
  const secondContainsFirst =
    secondCoversFirst &&
    (second.left < first.left || second.right > first.right);
  const overlapWidth = Math.min(first.right, second.right) - Math.max(first.left, second.left);

  if (relationship === "firstContainsSecond") return firstContainsSecond;
  if (relationship === "secondContainsFirst") return secondContainsFirst;
  return overlapWidth > 0 && !firstCoversSecond && !secondCoversFirst;
}

function createRandomData() {
  const relationshipRoll = Math.random();
  const relationship = relationshipRoll < 0.25
    ? "firstContainsSecond"
    : relationshipRoll < 0.5
    ? "secondContainsFirst"
    : "overlap";
  const isRoof = relationship === "firstContainsSecond"
    ? true
    : relationship === "secondContainsFirst"
    ? false
    : Math.random() < 0.5;

  for (let attempt = 0; attempt < 2000; attempt += 1) {
    const join = randomInt(-1, 1);
    const left = join - randomInt(3, 5);
    const right = join + randomInt(3, 5);
    if (left < -5 || right > 5) continue;
    const a1 = isRoof ? 1 : -1;
    const a2 = -a1;
    const firstJoinY = randomInt(-1, 2);
    const secondJoinY = firstJoinY + [-2, -1, 1, 2][randomInt(0, 3)];
    const b1 = firstJoinY - a1 * join;
    const b2 = secondJoinY - a2 * join;
    const base = { left, join, right, a1, b1, a2, b2, rightClosed: true };
    const graphValues = [
      valueAt(base, left),
      firstJoinY,
      secondJoinY,
      valueAt(base, right),
    ];
    if (graphValues.some((value) => value < -5 || value > 5)) continue;

    const firstRange = branchRange(
      a1,
      b1,
      { left, right: join, leftClosed: true, rightClosed: true }
    );
    const secondRange = branchRange(
      a2,
      b2,
      { left: join, right, leftClosed: false, rightClosed: true }
    );
    if (!matchesRangeRelationship(firstRange, secondRange, relationship)) continue;
    const ranges = mergeIntervals([firstRange, secondRange]);
    if (ranges.length !== 1) continue;

    const equation = findEquation(base);
    const comparison = findComparison(base);
    if (!equation || !comparison) continue;

    const windowLeft = join;
    const windowRight = join + 1;
    const secondRightY = valueAt(base, windowRight);
    const secondSupremum = a2 < 0 ? secondJoinY : secondRightY;
    if (firstJoinY <= secondSupremum) continue;
    const windowMax = firstJoinY;

    return {
      ...base,
      ...equation,
      windowLeft,
      windowRight,
      windowMax,
      range: ranges[0],
      ...comparison,
      greaterInterval: comparison.comparisonInterval,
      maxPoints: 4,
    };
  }

  return makeOriginalData();
}

function makeOriginalData() {
  const ys = [
    valueAt(ORIGINAL, ORIGINAL.left),
    valueAt(ORIGINAL, ORIGINAL.join),
    valueAt(ORIGINAL, ORIGINAL.right),
  ];
  return {
    ...ORIGINAL,
    range: {
      left: Math.min(...ys),
      right: Math.max(...ys),
      leftClosed: true,
      rightClosed: true,
    },
    greaterInterval: ORIGINAL.comparisonInterval,
    maxPoints: 4,
  };
}

export function originalData() {
  return makeOriginalData();
}

export function generateTask(options = {}) {
  const isOriginal = options.variant === "original" && options.session === "2026_MAJ";
  return {
    type: "MaturaPiecewiseReadingOpenTask",
    data: isOriginal ? makeOriginalData() : createRandomData(),
    source: {
      variant: isOriginal ? "original" : "random",
      exam: "MATURA",
      session: isOriginal ? "2026_MAJ" : undefined,
      taskNumber: "12",
      basedOn: "MMAP-P0-100-A-2605-12",
    },
  };
}

export function buildGraphModel(data) {
  const firstJoinY = data.a1 * data.join + data.b1;
  const secondJoinY = data.a2 * data.join + data.b2;
  const points = {
    left: { x: data.left, y: valueAt(data, data.left), markerSymbol: "circle-filled", showLabel: false },
    firstJoin: { x: data.join, y: firstJoinY, markerSymbol: "circle-filled", showLabel: false },
    right: {
      x: data.right,
      y: valueAt(data, data.right),
      markerSymbol: data.rightClosed === false ? "circle-open" : "circle-filled",
      showLabel: false,
    },
  };

  if (Math.abs(firstJoinY - secondJoinY) > EPS) {
    points.secondJoin = {
      x: data.join,
      y: secondJoinY,
      markerSymbol: "circle-open",
      showLabel: false,
    };
  }

  return {
    points,
    curves: [
      { x: [data.left, data.join], y: [valueAt(data, data.left), firstJoinY], width: 5 },
      { x: [data.join, data.right], y: [secondJoinY, valueAt(data, data.right)], width: 5 },
    ],
  };
}

function Graph({ data }) {
  const graph = buildGraphModel(data);
  return <MaturaFunctionGraph points={graph.points} curves={graph.curves} xRange={[-6, 6]} yRange={[-6, 6]} />;
}

export function buildExplanationGraphModel(data) {
  const graph = buildGraphModel(data);
  const interval = data.comparisonInterval;
  const guideColor = "#8b5cf6";
  return {
    points: graph.points,
    curves: [
      ...graph.curves,
      { x: [-6, 6], y: [data.threshold, data.threshold], color: guideColor, width: 2, dash: "dot" },
    ],
    verticalBands: buildInequalityHighlightBands({
      intervals: interval,
      threshold: data.threshold,
      operator: data.comparisonOperator,
    }),
  };
}

function ExplanationGraph({ data }) {
  const graph = buildExplanationGraphModel(data);
  return (
    <MaturaFunctionGraph
      points={graph.points}
      curves={graph.curves}
      verticalBands={graph.verticalBands}
      xRange={[-6, 6]}
      yRange={[-6, 6]}
    />
  );
}

export function renderTask({ data }) {
  const first = formatLinear(data.a1, data.b1);
  const second = formatLinear(data.a2, data.b2);
  const comparison = COMPARISON_LATEX[data.comparisonOperator];
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <p>Funkcja <InlineMath math="f" /> jest określona wzorem:</p>
      <BlockMath math={`f(x)=\\begin{cases}${first},&\\text{dla } x\\in[${data.left},${data.join}],\\\\${second},&\\text{dla } x\\in(${data.join},${data.right}${data.rightClosed === false ? ")" : "]"}.\\end{cases}`} />
      <Graph data={data} />
      <ol>
        <li>Podaj rozwiązanie równania <InlineMath math={`f(x)=${data.equationY}`} />.</li>
        <li>Podaj największą wartość funkcji <InlineMath math="f" /> w przedziale <InlineMath math={`[${data.windowLeft},${data.windowRight}]`} />.</li>
        <li>Podaj zbiór wartości funkcji <InlineMath math="f" />.</li>
        <li>Podaj zbiór argumentów, dla których <InlineMath math={`f(x)${comparison}${data.threshold}`} />.</li>
      </ol>
    </div>
  );
}

export function renderInput(_task, value = {}, onChange, disabled) {
  const set = (key, next) => onChange?.({ ...value, [key]: next });
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>1. <InlineMath math="x=" /><FractionInput value={value.equationX || ""} onChange={(next) => set("equationX", next)} disabled={disabled} label="x" /></div>
      <div>2. Największa wartość: <FractionInput value={value.windowMax || ""} onChange={(next) => set("windowMax", next)} disabled={disabled} label={"f_{\\max}"} /></div>
      <div>3. Zbiór wartości: <IntervalInput value={value.range || ""} onChange={(next) => set("range", next)} disabled={disabled} /></div>
      <div>4. Argumenty: <IntervalInput value={value.comparison || ""} onChange={(next) => set("comparison", next)} disabled={disabled} /></div>
    </div>
  );
}

function intervalMatches(value, expected) {
  const parsed = parseSingleInterval(value?.raw ?? value ?? "");
  if (!parsed) return false;
  return Math.abs(parsed.left - expected.left) < EPS
    && Math.abs(parsed.right - expected.right) < EPS
    && parsed.leftClosed === expected.leftClosed
    && parsed.rightClosed === expected.rightClosed;
}

export function checkAnswer({ data }, value = {}) {
  const checks = [
    Math.abs(parseNumberFromAny(value.equationX) - data.equationX) < EPS,
    Math.abs(parseNumberFromAny(value.windowMax) - data.windowMax) < EPS,
    intervalMatches(value.range, data.range),
    intervalMatches(value.comparison || value.greater, data.comparisonInterval),
  ];
  const score = checks.filter(Boolean).length;
  return { correct: score === 4, score, maxScore: 4, details: checks };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <InlineMath math={`x=${data.equationX}`} />
      <InlineMath math={`f_{\\max}=${data.windowMax}`} />
      <InlineMath math={`W_f=${intervalLatex(data.range)}`} />
      <InlineMath math={`x\\in${intervalLatex(data.comparisonInterval)}`} />
    </div>
  );
}

export function renderUserAnswer(value = {}) {
  const comparison = value.comparison || value.greater;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {value.equationX && <InlineMath math={`x=${value.equationX?.raw ?? value.equationX}`} />}
      {value.windowMax && <InlineMath math={`f_{\\max}=${value.windowMax?.raw ?? value.windowMax}`} />}
      {value.range && <InlineMath math={`W_f=${value.range?.raw ?? value.range}`} />}
      {comparison && <InlineMath math={`x\\in${comparison?.raw ?? comparison}`} />}
    </div>
  );
}

export function renderExplanation({ data }) {
  const comparison = COMPARISON_LATEX[data.comparisonOperator];
  const usesFirstBranch = data.equationX <= data.join;
  const equationA = usesFirstBranch ? data.a1 : data.a2;
  const equationB = usesFirstBranch ? data.b1 : data.b2;
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <p><strong>Krok 1.</strong> Poziom <InlineMath math={`y=${data.equationY}`} /> przecina wykres w jednym punkcie. Korzystamy ze wzoru fragmentu, na którym leży ten punkt.</p>
      <BlockMath math={`${equationA}x+${equationB}=${data.equationY}\\Longrightarrow x=${data.equationX}`} />
      <p><strong>Krok 2.</strong> Przedział <InlineMath math={`[${data.windowLeft},${data.windowRight}]`} /> obejmuje wierzchołek wykresu, którego rzędna wynosi <InlineMath math={String(data.windowMax)} />.</p>
      <p><strong>Krok 3.</strong> Najmniejsza i największa osiągana rzędna wyznaczają zbiór wartości.</p>
      <BlockMath math={`W_f=${intervalLatex(data.range)}`} />
      <p><strong>Krok 4.</strong> Rysujemy prostą <InlineMath math={`y=${data.threshold}`} /> i odczytujemy argumenty, dla których wykres spełnia warunek <InlineMath math={`f(x)${comparison}${data.threshold}`} />.</p>
      <ExplanationGraph data={data} />
      <BlockMath math={`f(x)${comparison}${data.threshold}\\Longleftrightarrow x\\in${intervalLatex(data.comparisonInterval)}`} />
    </div>
  );
}
